import {
  type FC,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import {
  Alert,
  AppBar,
  Badge,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PeopleIcon from "@mui/icons-material/People";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "@/shared/hooks";
import { useUser } from "@/shared/hooks/use-user/useUser";
import { CreateChatMessageMutation } from "@/entities/contents";
import type { CreateChatMessageMutationType } from "@/types/graphql/site.generated";
import type { ContentChatMode } from "./ContentChat";
import type { ChatMessageListItemType } from "./types";

const MAX_MESSAGE_LENGTH = 200;

export interface ChatMention {
  userId: string;
  displayName: string;
}

interface ChatHeaderWithFormProps {
  chatRoomId: string;
  mode: ContentChatMode;
  viewerBan?: { expiredAt: string } | null;
  onlineCount: number;
  replyTo: ChatMessageListItemType | null;
  onCancelReply: () => void;
  // Упоминание ставится кликом по аватарке в ленте, как в референсе:
  // отдельной кнопки у поля ввода нет.
  mention: ChatMention | null;
  onClearMention: () => void;
  // Успешная отправка своего сообщения: лента должна вернуться к низу,
  // даже если зритель перед этим отлистал её вверх.
  onMessageSent?: () => void;
  onOpenOnlineList: () => void;
  onOpenSoundSettings: (anchor: HTMLElement) => void;
}

export const ChatHeaderWithForm: FC<
  PropsWithChildren<ChatHeaderWithFormProps>
> = ({
  chatRoomId,
  mode,
  viewerBan,
  onlineCount,
  replyTo,
  onCancelReply,
  mention,
  onClearMention,
  onMessageSent,
  onOpenOnlineList,
  onOpenSoundSettings,
  children,
}) => {
  const { t } = useTranslation();
  const { user, openSignIn } = useUser();
  const isStream = mode === "stream";
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Референсное поведение: выбранное упоминание дописывается в текст как
  // "@Имя " (второй раз то же имя не дублируется) и фокус уходит в поле.
  useEffect(() => {
    if (!mention) return;
    setMessage((prev) => {
      const mentionText = `@${mention.displayName}`;
      if (prev.includes(mentionText)) return prev;
      return `${prev}${mentionText} `;
    });
    inputRef.current?.focus();
  }, [mention]);

  const [createChatMessage, { loading }] =
    useMutation<CreateChatMessageMutationType>(CreateChatMessageMutation, {
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed) return;

    const result = await createChatMessage({
      variables: {
        input: {
          chatRoomId,
          message: trimmed,
          mentionUserId: mention?.userId,
          replyToMessageId: replyTo ? String(replyTo.id) : undefined,
        },
      },
    });

    if (!result.data?.createChatMessage) {
      // result.errors !== undefined значит настоящая GraphQL-ошибка (rate-limit,
      // бан и т.п.) — на неё уже показан тост через onError выше, здесь не
      // дублируем. Только когда errors нет, data пуста именно из-за
      // референсного поведения: сообщение, похожее на ссылку, молча не
      // создаётся на бэкенде (createChatMessage возвращает null, а не
      // ошибку) — без этого предупреждения зритель видел бы только
      // очистившееся поле ввода без всякого объяснения.
      if (!result.errors) {
        enqueueSnackbar(t("stream.chat.messageBlocked"), {
          variant: "warning",
        });
      }
      return;
    }

    setMessage("");
    onClearMention();
    onCancelReply();
    onMessageSent?.();
  };

  const title = isStream ? null : (
    <Typography variant="subtitle1" sx={{ px: 1.5, pt: 1.5 }}>
      {t("contentChat.title")}
    </Typography>
  );

  const bar = (
    <AppBar position="static" color="default" elevation={0} sx={{ zIndex: 2 }}>
      {viewerBan ? (
        <Alert severity="warning" sx={{ m: 1 }}>
          {t("stream.chat.bannedMessage")}
        </Alert>
      ) : (
        <>
          {replyTo && (
            <Box
              sx={{
                borderLeft: 2,
                borderColor: "primary.main",
                mx: 1.5,
                mt: 1,
                pl: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" noWrap>
                {t("stream.chat.replyingTo", {
                  name: replyTo.userDisplayName ?? "",
                })}
              </Typography>
              <IconButton size="small" onClick={onCancelReply}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Toolbar
            sx={{ pl: "12px !important", pr: "12px !important", gap: 1 }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={
                user
                  ? t("stream.chat.placeholder")
                  : t("contentChat.signInToWrite")
              }
              value={message}
              // Гость видит ленту, но писать не может: поле только для
              // чтения, а клик или Enter/Space по нему открывает окно
              // входа (референсное поведение .js-sign-in). Через onFocus
              // это делать нельзя: окно выскакивало бы и на проходе Tab.
              inputRef={inputRef}
              slotProps={{
                input: {
                  readOnly: !user,
                  // Кнопка отправки внутри поля, как в референсе: снаружи
                  // остаются только звук и список онлайна.
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        disabled={loading || !user}
                        onClick={() => void handleSend()}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(event) =>
                setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH))
              }
              onClick={() => {
                if (!user) openSignIn();
              }}
              onKeyDown={(event) => {
                if (!user) {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openSignIn();
                  }
                  return;
                }
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            />
            {isStream && (
              <IconButton
                size="small"
                onClick={(event) => onOpenSoundSettings(event.currentTarget)}
              >
                <VolumeUpIcon fontSize="small" />
              </IconButton>
            )}
            {isStream && (
              <IconButton size="small" onClick={onOpenOnlineList}>
                <Badge badgeContent={onlineCount} color="success" max={99}>
                  <PeopleIcon fontSize="small" />
                </Badge>
              </IconButton>
            )}
          </Toolbar>
        </>
      )}
    </AppBar>
  );

  // Чат эфира: форма сверху, над лентой (референсный Chat.js). Комментарии
  // под видео читаются как комментарии: заголовок сверху, лента, форма
  // внизу, поэтому лента приходит сюда через children и встаёт между ними.
  // Заголовок остаётся на месте и у забаненного зрителя: ниже вместо формы
  // просто показывается предупреждение о бане.
  if (isStream) {
    return bar;
  }

  return (
    <>
      {title}
      {children}
      {bar}
    </>
  );
};
